import React, { useState, useCallback, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router';
import { FaArrowLeft } from 'react-icons/fa';
import { Header } from './docs/Header';
import { MerchantPanel } from './analytics/MerchantPanel';
import { MenuPanel } from './analytics/MenuPanel';
import { DateRangeDropdown } from './analytics/DateRangeDropdown';
import { DateRangePickers } from './analytics/DateRangePickers';
import { DateRangeButtons } from './analytics/DateRangeButtons';
import { MerchantCountBadge } from './analytics/MerchantCountBadge';
import { fetchMenuRequest } from '../redux/slices/menuSlice';
import { addDateRangeParams } from '../utils/dateUtils';
import { DATE_RANGE_OPTIONS } from '../utils/dateRangeUtils';
import { useDateRange } from '../hooks/useDateRange';
import { useAnalytics } from '../hooks/useAnalytics';
import { analyticsService } from '../services/analyticsService';
import type { RootState } from '../redux/store';

interface Restaurant {
  restaurantId: string;
  name: string;
  address: string;
  phone: string | null;
  cuisineType: string | null;
  image: string | null;
}

export const Analytics: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { menuItems, loading: menuLoading } = useSelector((state: RootState) => state.menu);
  const authState = useSelector((state: RootState) => state.auth);

  
  const [selectedRestaurant, setSelectedRestaurant] = useState<Restaurant | null>(null);
  const [menuStatus, setMenuStatus] = useState<any>(null);
  const [menuType, setMenuType] = useState<'food' | 'drinks'>('food');

  // Custom hooks for clean separation of concerns
  const {
    rangeType,
    startDate,
    endDate,
    handleStartDateChange,
    handleEndDateChange,
    handleRangeChange
  } = useDateRange('custom');

  const { franchises, loading: franchisesLoading, fetchData } = useAnalytics({
    accountId: authState.currentApiKey || undefined,
    apiBaseUrl: authState.apiBaseUrl
  });

  // Handle range type change from dropdown
  const onRangeChange = useCallback((newValue: string) => {
    handleRangeChange(newValue as any, fetchData);
  }, [handleRangeChange, fetchData]);

  // Handle Apply button click for custom range
  const handleApply = useCallback(async () => {
    if (!startDate || !endDate) return;
    
    await fetchData(startDate, endDate);
    
    // If merchant is selected, also call menu API
    if (selectedRestaurant) {
      const params: any = { restaurantId: selectedRestaurant.restaurantId };
      addDateRangeParams(params, startDate, endDate);
      dispatch(fetchMenuRequest(params));
    }
  }, [startDate, endDate, selectedRestaurant, fetchData, dispatch]);

  // Handle menu type change
  const handleMenuTypeChange = useCallback((newType: 'food' | 'drinks') => {
    setMenuType(newType);
    
    // Refetch menu if restaurant is selected
    if (selectedRestaurant) {
      dispatch(fetchMenuRequest({
        restaurantId: selectedRestaurant.restaurantId,
        type: newType
      }));
    }
  }, [selectedRestaurant, dispatch]);

  // Handle restaurant click
  const handleRestaurantClick = useCallback(async (restaurant: Restaurant) => {
    setSelectedRestaurant(restaurant);
    
    // Menu API with type parameter
    dispatch(fetchMenuRequest({ 
      restaurantId: restaurant.restaurantId,
      type: menuType
    }));

    // Fetch restaurant menu status
    if (authState.currentApiKey && authState.apiBaseUrl) {
      try {
        const status = await analyticsService.getRestaurantMenuStatus(
          authState.apiBaseUrl,
          authState.currentApiKey,
          restaurant.restaurantId
        );
        setMenuStatus(status);
      } catch (error) {
        console.error('Failed to fetch restaurant menu status:', error);
        setMenuStatus(null);
      }
    }
  }, [dispatch, authState.currentApiKey, authState.apiBaseUrl, menuType]);

  // Memoize menu items for performance
  const displayMenuItems = useMemo(() => {
    return menuItems || [];
  }, [menuItems]);

  // Memoize restaurants array to maintain stable reference
  const restaurantsList = useMemo(() => {
    return franchises.map(f => ({
      restaurantId: f.restaurant_id,
      name: f.restaurant_name,
      address: f.address,
      phone: f.phone,
      cuisineType: f.cusine_type || null,
      image: f.image
    }));
  }, [franchises]);

  const isCustomRange = rangeType === 'custom';

  return (
    <div className="h-screen bg-[var(--bg-primary)] flex flex-col overflow-hidden">
      <Header />
      
      <main className="w-full px-8 py-4 flex-1 flex flex-col overflow-hidden">
        {/* Header Container - Separate from content */}
        <div className="flex-shrink-0 space-y-4 mb-4">
          {/* Back Button */}
          <div>
            <button
              onClick={() => navigate(-1)}
              className="flex items-center space-x-2 px-4 py-2 text-sm font-medium text-[var(--text-secondary)] hover:text-[#EF4421] transition-colors rounded-md hover:bg-[var(--bg-secondary)]"
              aria-label="Go back"
            >
              <FaArrowLeft className="w-4 h-4" />
              <span>Back</span>
            </button>
          </div>

          {/* Page Title and Filters - Same Row */}
          <div className="flex items-end gap-8">
            {/* Title */}
            <div className="flex items-center space-x-3 flex-shrink-0">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center shadow-lg" style={{ background: 'linear-gradient(to bottom right, #FF6B4A, #EF4421)' }}>
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </div>
              <div>
                <h1 className="text-xl font-bold text-[var(--text-primary)]">
                  Analytics Dashboard
                </h1>
                <p className="text-sm text-[var(--text-secondary)]">
                  Monitor your business performance
                </p>
              </div>
            </div>

            {/* Filters - Natural width, no flex-1 */}
            <div className="flex items-end gap-2">
              {/* 1. Date Range Dropdown */}
              <div className="w-44 md:w-48 lg:w-52">
                <DateRangeDropdown
                  value={rangeType}
                  onChange={onRangeChange}
                  options={DATE_RANGE_OPTIONS}
                />
              </div>

              {/* 2. Date Pickers - Only when Custom */}
              {isCustomRange && (
                <DateRangePickers
                  startDate={startDate}
                  endDate={endDate}
                  onStartDateChange={handleStartDateChange}
                  onEndDateChange={handleEndDateChange}
                />
              )}

              {/* 3. Action Buttons - Only when Custom */}
              {isCustomRange && (
                <DateRangeButtons
                  onApply={handleApply}
                  isLoading={franchisesLoading}
                />
              )}
            </div>

            {/* Merchant Count Badge - Always far right */}
            <div className="ml-auto">
              <MerchantCountBadge count={franchises.length} />
            </div>
          </div>
        </div>

        {/* Content Container - Separate from header */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Conditional Panel Layout */}
          {franchises.length === 0 && !franchisesLoading ? (
            // No Merchants - Full Width Empty State
            <div className="flex-1 flex items-center justify-center bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg p-8">
              <div className="text-center">
                <svg className="w-16 h-16 mx-auto mb-4 text-[var(--text-muted)] opacity-30" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
                <p className="text-lg font-medium text-[var(--text-secondary)]">No merchants found for the selected date range.</p>
                <p className="text-sm text-[var(--text-muted)] mt-2">Please choose a different range and try again.</p>
              </div>
            </div>
          ) : (
            // Has Merchants - 2-Panel Layout
            <div className="flex gap-4 flex-1 overflow-hidden">
              <MerchantPanel
                restaurants={restaurantsList}
                selectedRestaurant={selectedRestaurant}
                onRestaurantClick={handleRestaurantClick}
                loading={franchisesLoading}
              />
              <MenuPanel
                selectedRestaurant={selectedRestaurant}
                menuItems={displayMenuItems}
                loading={menuLoading}
                menuStatus={menuStatus}
                menuType={menuType}
                onMenuTypeChange={handleMenuTypeChange}
              />
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default Analytics;
